import { cn } from "@/lib/utils";
import { type Task, dueDays, dueDistance, shortDateLabel } from "@/pages/Tasks/task";

// One labelled date line. `sub` is the muted part after the date: how late,
// or how far away.
export function dueLine(dueOn: string, label: string): { text: string; sub?: string; className: string } {
  const days = dueDays(dueOn);
  if (days < 0) {
    return {
      text: `${label}: ${shortDateLabel(dueOn)}`,
      sub: `${-days} day${days === -1 ? "" : "s"} overdue`,
      className: "text-destructive font-semibold",
    };
  }
  if (days === 0) return { text: `${label}: today`, className: "text-warning font-semibold" };
  if (days === 1) return { text: `${label}: tomorrow`, className: "text-foreground" };
  return { text: `${label}: ${shortDateLabel(dueOn)}`, sub: dueDistance(dueOn), className: "text-foreground" };
}

export function DueLine({ due }: { due: ReturnType<typeof dueLine> }) {
  return (
    <p className={cn("mt-0.5 text-[13px]", due.className)}>
      {due.text}
      {due.sub && <span className="ml-1 text-muted-foreground">{due.sub}</span>}
    </p>
  );
}

// "Your due date" only while the task waits on the viewer, then the task
// overall due date, which everyone sees (the doer included).
export default function DueDates({ task }: { task: Pick<Task, "due_on" | "my_due_on"> }) {
  if (!task.my_due_on) return <DueLine due={dueLine(task.due_on, "Task overall due date")} />;

  return (
    <>
      <DueLine due={dueLine(task.my_due_on, "Your due date")} />
      <p className="mt-0.5 text-[13px] text-foreground">Task overall due date: {shortDateLabel(task.due_on)}</p>
    </>
  );
}
